import { motion } from 'framer-motion'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import {
  cancelCashout,
  formatCents,
  getAgeStatus,
  submitDateOfBirth,
  type AgeStatus,
  buyPrizeCoins,
  redeemPrizeCoins,
  getCashBalance,
  getPaymentsConfig,
  quoteCashout,
  requestCashout,
  startCheckout,
  type CashBalance,
  type PaymentsConfig,
} from '../lib/payments'
import { Icon, Spinner } from '../components/Icon'
import { Button, Panel, TextField, cx } from '../components/ui'

export function WalletScreen({ onBack }: { onBack: () => void }) {
  const { user, setBalance: setPurseBalance } = useAuth()
  const [config, setConfig] = useState<PaymentsConfig | null>(null)
  const [balance, setBalance] = useState<CashBalance | null>(null)
  const [age, setAge] = useState<AgeStatus | null>(null)
  const [dob, setDob] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [amount, setAmount] = useState('10')
  const [paypal, setPaypal] = useState('')
  const [convertAmount, setConvertAmount] = useState('10')
  const [convertDirection, setConvertDirection] = useState<'buy' | 'redeem'>('buy')

  const refresh = useCallback(async () => {
    const [cfg, bal, ageStatus] = await Promise.all([
      getPaymentsConfig(),
      getCashBalance(),
      getAgeStatus(),
    ])
    setConfig(cfg)
    setBalance(bal)
    setAge(ageStatus)
    // The socket pushes coin balances too, but a conversion is an HTTP call, so
    // nothing would tell the header about it otherwise.
    setPurseBalance('prize', bal.prizeCoins)
    setPurseBalance('practice', bal.practiceCoins)
  }, [setPurseBalance])

  useEffect(() => {
    refresh()
      .catch((caught) => setError(caught instanceof Error ? caught.message : 'Could not load wallet.'))
      .finally(() => setLoading(false))
  }, [refresh])

  // Stripe sends the player back with a query flag. The balance is credited by
  // the webhook, not by this redirect, so it may lag by a second or two.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const purchase = params.get('purchase')
    if (!purchase) return
    if (purchase === 'success') {
      setNotice('Payment received. Your balance updates as soon as Stripe confirms it.')
      const timer = window.setTimeout(() => void refresh(), 2500)
      window.history.replaceState({}, '', window.location.pathname)
      return () => window.clearTimeout(timer)
    }
    setNotice('Checkout cancelled. Nothing was charged.')
    window.history.replaceState({}, '', window.location.pathname)
    return
  }, [refresh])

  const buy = async (packageId: 'starter' | 'regular' | 'large') => {
    setError(null)
    setBusy(packageId)
    try {
      const url = await startCheckout(packageId)
      window.location.href = url
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not start checkout.')
      setBusy(null)
    }
  }

  const submitCashout = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setNotice(null)
    setBusy('cashout')
    try {
      const result = await requestCashout(Number(amount), paypal.trim())
      setNotice(result.message)
      await refresh()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not submit that request.')
    } finally {
      setBusy(null)
    }
  }

  const convert = async () => {
    setError(null)
    setNotice(null)
    setBusy('convert')
    try {
      const dollars = Math.floor(Number(convertAmount) || 0)
      const result =
        convertDirection === 'buy'
          ? await buyPrizeCoins(dollars * 100)
          : await redeemPrizeCoins(dollars * (config?.coinsPerDollar ?? 100))
      setNotice(
        convertDirection === 'buy'
          ? `${formatCents(result.movedCents)} is now in prize coins, ready to stake.`
          : `${formatCents(result.movedCents)} moved back to your withdrawable balance.`,
      )
      await refresh()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not convert that amount.')
    } finally {
      setBusy(null)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner size={22} className="text-fg-dim" />
      </div>
    )
  }

  const pending = balance?.cashouts.find((c) => c.status === 'requested') ?? null
  const feeRate = config?.cashout.feeRate ?? 0.15
  const preview = quoteCashout(Number(amount) || 0, feeRate)
  const available = balance?.availableCents ?? 0
  const prizeCoins = balance?.prizeCoins ?? user?.prizeCoins ?? 0
  const practiceCoins = balance?.practiceCoins ?? user?.balance ?? 0
  const canConvertIn = (config?.moneyAllowed ?? false) && (age?.verified ?? false)
  const convertible = (Number(convertAmount) || 0) > 0
  const canRequest =
    available >= (config?.cashout.minimumCents ?? 1000) &&
    !pending &&
    (config?.moneyAllowed ?? false) &&
    (age?.verified ?? false)

  return (
    <div className="min-h-dvh pad-safe pad-safe-top">
      <header className="mx-auto flex w-full max-w-2xl items-center justify-between py-4">
        <button
          type="button"
          onClick={onBack}
          className="flex h-11 items-center gap-2 rounded-xl pr-3 text-sm text-fg-muted transition-colors hover:text-fg"
        >
          <Icon name="arrowLeft" size={18} />
          Back
        </button>
        <span className="eyebrow">Wallet</span>
      </header>

      <main className="mx-auto w-full max-w-2xl pb-16">
        {/* Balance. Three numbers that behave differently, so each is labelled
            with what it can actually do rather than left to be inferred. */}
        <Panel className="mb-5 p-6 text-center">
          <p className="eyebrow">Withdrawable balance</p>
          <p className="mt-3 font-mono text-[clamp(2.5rem,11vw,3.5rem)] font-bold leading-none tnum text-gold">
            {formatCents(balance?.cashCents ?? 0)}
          </p>
          {(balance?.pendingCashoutCents ?? 0) > 0 && (
            <p className="mt-3 text-sm text-fg-muted">
              {formatCents(balance?.pendingCashoutCents ?? 0)} is held against a pending cashout.
            </p>
          )}
          <div className="hairline my-5" />
          <div className="grid grid-cols-2 gap-4 text-left">
            <div>
              <p className="eyebrow text-fg-dim">Prize coins</p>
              <p className="mt-1.5 font-mono text-xl font-semibold tnum text-lime">
                {formatCents(prizeCoins)}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-fg-dim">
                Staked against real people. Converts back to cash any time.
              </p>
            </div>
            <div>
              <p className="eyebrow text-fg-dim">Practice coins</p>
              <p className="mt-1.5 font-mono text-xl font-semibold tnum text-gold">
                {practiceCoins.toLocaleString()}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-fg-dim">
                Free play against the bot. Never withdrawable, in either direction.
              </p>
            </div>
          </div>
        </Panel>

        {/* Conversion. Cash and prize coins are the same money in two states:
            one you can withdraw, one you can stake. */}
        <Panel className="mb-5 p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="eyebrow">Move money</p>
            <div className="flex rounded-lg border border-ink-700 p-0.5">
              {(['buy', 'redeem'] as const).map((direction) => (
                <button
                  key={direction}
                  type="button"
                  onClick={() => setConvertDirection(direction)}
                  className={cx(
                    // Tall enough to hit on a phone: this switches which way
                    // real money moves, and it was 28px high.
                    'flex min-h-11 items-center rounded-[6px] px-3 text-xs font-medium transition-colors',
                    convertDirection === direction
                      ? 'bg-ink-750 text-fg'
                      : 'text-fg-dim hover:text-fg-muted',
                  )}
                >
                  {direction === 'buy' ? 'To prize coins' : 'To cash'}
                </button>
              ))}
            </div>
          </div>

          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              void convert()
            }}
          >
            <div className="flex-1">
              <TextField
                label={convertDirection === 'buy' ? 'Dollars to convert' : 'Dollars to release'}
                type="number"
                min="1"
                step="1"
                value={convertAmount}
                onChange={(e) => setConvertAmount(e.target.value)}
                className="font-mono tnum"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              variant="outline"
              loading={busy === 'convert'}
              disabled={!convertible || (convertDirection === 'buy' && !canConvertIn)}
            >
              {convertDirection === 'buy' ? 'Convert' : 'Release'}
              <Icon name="arrowRight" size={16} />
            </Button>
          </form>

          <p className="mt-3 text-xs leading-relaxed text-fg-dim">
            {convertDirection === 'buy'
              ? `Moves cash into prize coins so you can stake it. ${(config?.coinsPerDollar ?? 100).toLocaleString()} coins per dollar, and the rate is identical coming back \u2014 converting never costs you anything.`
              : 'Moves prize coins back into your withdrawable balance. Coins staked in a match that has already started are not included.'}
          </p>
          {convertDirection === 'buy' && !canConvertIn && (
            <p className="mt-2 text-xs text-fg-muted">
              {config?.moneyAllowed === false
                ? (config.moneyBlockedMessage ?? 'Prize play is not available where you are.')
                : 'Confirm your date of birth below before converting.'}
            </p>
          )}
        </Panel>

        {error && (
          <p role="alert" className="mb-4 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            <Icon name="close" size={16} className="mt-0.5 shrink-0" />
            {error}
          </p>
        )}
        {notice && (
          <p className="mb-4 flex items-start gap-2 rounded-xl border border-lime/25 bg-lime-wash px-4 py-3 text-sm text-lime">
            <Icon name="check" size={16} className="mt-0.5 shrink-0" />
            {notice}
          </p>
        )}

        {/* Where cash play is not permitted, say so once at the top rather
            than letting the player fill in a form that will be refused. */}
        {config && !config.moneyAllowed && (
          <div className="mb-6 rounded-2xl border border-gold/30 bg-gold/8 p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-gold">
              <Icon name="lock" size={16} />
              Cash play is not available here
            </p>
            <p className="mt-2 text-sm leading-relaxed text-fg-muted">
              {config.moneyBlockedMessage}
            </p>
            {config.location.region && (
              <p className="mt-2 font-mono text-xs text-fg-dim">
                Detected: {config.location.region}, {config.location.country}
              </p>
            )}
            <p className="mt-3 text-xs leading-relaxed text-fg-dim">
              Everything else still works. Coin matches and practice rounds are
              free and open everywhere.
            </p>
          </div>
        )}

        {/* Age gate. Only asked once the region permits money at all - there
            is no point collecting a birthday from someone who cannot pay
            regardless. */}
        {config?.moneyAllowed && age && !age.verified && (
          <Panel className="mb-6 p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-fg">
              <Icon name="lock" size={16} className="text-gold" />
              Confirm your age
            </p>
            <p className="mt-2 text-sm leading-relaxed text-fg-muted">
              You must be {age.requiredAge} or over to play for money where you are.
              This is asked once.
            </p>
            <form
              className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end"
              onSubmit={async (event) => {
                event.preventDefault()
                setError(null)
                setBusy('age')
                try {
                  await submitDateOfBirth(dob)
                  await refresh()
                  setNotice('Age confirmed.')
                } catch (caught) {
                  setError(caught instanceof ApiError ? caught.message : 'Could not confirm that.')
                } finally {
                  setBusy(null)
                }
              }}
            >
              <div className="flex-1">
                <TextField
                  label="Date of birth"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="font-mono"
                />
              </div>
              <Button type="submit" size="lg" loading={busy === 'age'} disabled={!dob}>
                Confirm
              </Button>
            </form>
          </Panel>
        )}

        {/* Buy */}
        <section className="mb-6">
          <p className="eyebrow mb-3">Add funds</p>
          {config?.enabled ? (
            <div className="grid gap-3 sm:grid-cols-3">
              {config.packages.map((pkg) => (
                <motion.button
                  key={pkg.id}
                  type="button"
                  onClick={() => void buy(pkg.id)}
                  disabled={busy !== null || !config.moneyAllowed || !(age?.verified ?? false)}
                  whileTap={{ scale: 0.985 }}
                  className="no-tap-delay flex min-h-[104px] flex-col items-center justify-center rounded-2xl border border-ink-700 bg-ink-900 p-5 transition-colors duration-200 hover:border-lime/45 disabled:opacity-40"
                >
                  <span className="font-mono text-2xl font-bold tnum text-fg">{pkg.label}</span>
                  <span className="mt-1.5 text-xs text-fg-dim">
                    {busy === pkg.id ? 'Opening checkout…' : 'Pay with card'}
                  </span>
                </motion.button>
              ))}
            </div>
          ) : (
            <Panel flush className="p-5">
              <p className="text-sm text-fg-muted">
                Card payments are not switched on for this server yet.
              </p>
            </Panel>
          )}
          <p className="mt-3 text-xs text-fg-dim">
            Payments are processed by Stripe. Your card details never reach ClickLinx.
          </p>
        </section>

        {/* Cash out */}
        <section className="mb-6">
          <p className="eyebrow mb-3">Cash out</p>
          <Panel flush className="p-5">
            {pending ? (
              <div>
                <p className="text-sm text-fg">
                  You have a cashout of{' '}
                  <span className="font-mono font-semibold tnum text-gold">
                    {formatCents(pending.netCents)}
                  </span>{' '}
                  on the way to {pending.paypalEmail}.
                </p>
                <p className="mt-2 text-xs text-fg-dim">
                  Payouts are sent within {config?.cashout.settlementWindow ?? '24 to 48 hours'}.
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-4"
                  loading={busy === 'cancel'}
                  onClick={async () => {
                    setBusy('cancel')
                    try {
                      await cancelCashout(pending.id)
                      await refresh()
                      setNotice('Cashout cancelled.')
                    } catch (caught) {
                      setError(caught instanceof ApiError ? caught.message : 'Could not cancel.')
                    } finally {
                      setBusy(null)
                    }
                  }}
                >
                  Cancel this request
                </Button>
              </div>
            ) : (
              <form onSubmit={submitCashout} className="space-y-4" noValidate>
                <TextField
                  label="Amount in whole dollars"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
                  inputMode="numeric"
                  className="font-mono"
                  hint={`Minimum balance ${formatCents(config?.cashout.minimumCents ?? 1000)}. A ${Math.round(feeRate * 100)}% fee applies.`}
                />

                <TextField
                  label="PayPal email"
                  type="email"
                  value={paypal}
                  onChange={(e) => setPaypal(e.target.value)}
                  placeholder="you@example.com"
                  icon={<Icon name="mail" size={17} />}
                  autoComplete="email"
                />

                {/* The split is shown before submitting, so the fee is never a
                    surprise after the fact. */}
                <div className="rounded-xl border border-ink-700 bg-ink-950/50 p-4 text-sm">
                  <Row label="You withdraw" value={formatCents(preview.grossCents)} />
                  <Row
                    label={`Service fee (${Math.round(feeRate * 100)}%)`}
                    value={`- ${formatCents(preview.feeCents)}`}
                    muted
                  />
                  <div className="hairline my-2.5" />
                  <Row label="Sent to PayPal" value={formatCents(preview.netCents)} strong />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  loading={busy === 'cashout'}
                  disabled={!canRequest || preview.grossCents <= 0 || preview.grossCents > available}
                >
                  Request cashout
                </Button>

                {!canRequest && (
                  <p className="text-center text-xs text-fg-dim">
                    You need at least {formatCents(config?.cashout.minimumCents ?? 1000)} to
                    cash out.
                  </p>
                )}
              </form>
            )}
          </Panel>
        </section>

        {/* History */}
        {(balance?.cashouts.length ?? 0) > 0 && (
          <section>
            <p className="eyebrow mb-3">Cashout history</p>
            <div className="space-y-2">
              {balance?.cashouts.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center justify-between rounded-xl border border-ink-700 px-4 py-3"
                >
                  <div>
                    <p className="font-mono text-sm tnum text-fg">{formatCents(row.netCents)}</p>
                    <p className="mt-0.5 text-xs text-fg-dim">
                      {new Date(row.createdAt).toLocaleDateString()} · fee{' '}
                      {formatCents(row.feeCents)}
                    </p>
                  </div>
                  <StatusPill status={row.status} />
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="mt-10 text-center text-xs leading-relaxed text-fg-dim">
          Cash balances are real money. Play coins are not and cannot be withdrawn.
        </p>
      </main>
    </div>
  )
}

function Row({
  label,
  value,
  muted,
  strong,
}: {
  label: string
  value: string
  muted?: boolean
  strong?: boolean
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={cx('text-sm', muted ? 'text-fg-dim' : 'text-fg-muted')}>{label}</span>
      <span
        className={cx(
          'font-mono text-sm tnum',
          strong ? 'font-semibold text-gold' : muted ? 'text-fg-dim' : 'text-fg',
        )}
      >
        {value}
      </span>
    </div>
  )
}

export function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'paid'
      ? 'border-lime/35 bg-lime-wash text-lime'
      : status === 'requested'
        ? 'border-gold/30 bg-gold/10 text-gold'
        : 'border-ink-700 bg-ink-850 text-fg-dim'

  return (
    <span
      className={cx(
        'rounded-full border px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-wider',
        tone,
      )}
    >
      {status}
    </span>
  )
}
